import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {SearchObjectResult} from '../../shared/search-object-result.model';
import {HttpUtils} from '../../shared/http-utils';
import {URL_BASE} from '../../core/constants';
import {map} from 'rxjs/operators';
import {LiteLoginSearchFilter} from './lite-login-search-filter.model';
import {LiteLoginAccess, LiteLoginAccessStatus, LiteLoginLoadInfo} from './lite-login-access.model';
import {SortDirection} from '../../shared/sort-direction';

export const LITE_LOGIN_ACCESSES_URI = 'lite-login/access';

@Injectable({
  providedIn: 'root'
})
export class LiteLoginService {

  constructor(
    private http: HttpClient,
  ) {}

  public search(
    page: number, size: number, direction: SortDirection, filters: LiteLoginSearchFilter
  ): Observable<SearchObjectResult<LiteLoginAccess>> {
    const { headers, params } = this.prepareSearchRequest({...filters}, page, size, direction);

    const url = `${URL_BASE}/api/${LITE_LOGIN_ACCESSES_URI}/search`;
    return this.http.get(url, { headers, params })
      .pipe(map(result => SearchObjectResult.fromDto(result, LiteLoginAccess.fromDto)));
  }

  public findById(id: string): Observable<LiteLoginAccess> {
    const headers = HttpUtils.createHeaders();
    const url = `${URL_BASE}/api/${LITE_LOGIN_ACCESSES_URI}/find/by/id/${id}`;

    return this.http.get(url, { headers })
      .pipe(map(result => LiteLoginAccess.fromDto(result)));
  }

  public create(access: LiteLoginAccess, carrierId: string, activate: boolean) {
    const headers = HttpUtils.createHeaders();
    const params = { activate: activate.toString() };
    const url = `${URL_BASE}/api/${LITE_LOGIN_ACCESSES_URI}`;
    const body = this.prepareCreateBody(LiteLoginAccess.toDto(access), carrierId);

    return this.http.post(url, body, { headers, params })
      .pipe(map(result => LiteLoginAccess.fromDto(result)));
  }

  public update(access: LiteLoginAccess, carrierId: string, activate: boolean) {
    const headers = HttpUtils.createHeaders();
    const params = { activate: activate.toString() };
    const url = `${URL_BASE}/api/${LITE_LOGIN_ACCESSES_URI}/${access.id}`;
    const body = this.prepareCreateBody(LiteLoginAccess.toDto(access), carrierId);

    return this.http.put(url, body, { headers, params })
      .pipe(map(result => LiteLoginAccess.fromDto(result)));
  }

  public activate(id: string): Observable<LiteLoginAccess> {
    const headers = HttpUtils.createHeaders();
    const url = `${URL_BASE}/api/${LITE_LOGIN_ACCESSES_URI}/activate/${id}`;

    return this.http.post(url, {}, { headers })
      .pipe(map(result => LiteLoginAccess.fromDto(result)));
  }

  public resendEmail(id: string): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url = `${URL_BASE}/api/${LITE_LOGIN_ACCESSES_URI}/resend-email/${id}`;

    return this.http.post<void>(url, {}, { headers });
  }

  public delete(id: string): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url = `${URL_BASE}/api/${LITE_LOGIN_ACCESSES_URI}/${id}`;

    return this.http.delete<void>(url, { headers });
  }

  public findLoadInfoByDeliveryNumber(deliveryNumber: string, carrierId: string): Observable<LiteLoginLoadInfo> {
    const headers = HttpUtils.createHeaders();
    const url = `${URL_BASE}/api/${LITE_LOGIN_ACCESSES_URI}/load-info/by/delivery-number/${deliveryNumber}`;
    const params = { carrierId };

    return this.http.get(url, {headers, params})
      .pipe(map(result => LiteLoginLoadInfo.fromDto(result)));
  }

  public findLoadInfoByLoadId(loadId: string, carrierId: string): Observable<LiteLoginLoadInfo> {
    const headers = HttpUtils.createHeaders();
    const url = `${URL_BASE}/api/${LITE_LOGIN_ACCESSES_URI}/load-info/by/load-id/${loadId}`;
    const params = { carrierId };

    return this.http.get(url, {headers, params})
      .pipe(map(result => LiteLoginLoadInfo.fromDto(result)));
  }

  private prepareSearchRequest(filters: any, page: number, size: number, direction: SortDirection) {
    filters = this.prepareFilters(filters);

    const headers = HttpUtils.createHeaders();
    const params  = {
      page: String(page),
      size: String(size),
      direction: direction,
      ...filters
    };
    return { headers, params };
  }

  private prepareFilters(filters: any) {
    if (!filters.emailAddress) {
      delete filters['emailAddress'];
    }

    if (!filters.filterNumber) {
      delete filters['filterNumber'];
    }

    if (!filters.createdAtAfter) {
      delete filters['createdAtAfter'];
    } else {
      filters.createdAtAfter = filters.createdAtAfter.toISOString();
    }

    if (!filters.createdAtBefore) {
      delete filters['createdAtBefore'];
    } else {
      filters.createdAtBefore = filters.createdAtBefore.endOf('day').toISOString();
    }

    if (!filters.status) {
      delete filters['status'];
    } else if (filters.status === LiteLoginAccessStatus.EXPIRED) {
      delete filters['status'];
      filters.onlyExpired = true;
    }

    return filters;
  }

  private prepareCreateBody(access: any, carrierId: string) {
    const params = {
      ...access,
      carrier_id: carrierId
    }

    if (!params.driver_name) {
      delete params['driver_name'];
    }
    if (!params.cabin_license_plate) {
      delete params['cabin_license_plate'];
    }
    if (!params.driver_id) {
      delete params['driver_id'];
    }
    if (!params.trailer_id) {
      delete params['trailer_id'];
    }
    if (!params.driver_phone_number) {
      delete params['driver_phone_number'];
    }

    return params;
  }
}
